import axiosInstance from './api/axiosInstance';

export interface OrderItem {
  itemType: 'product' | 'service';
  itemId: string;
  name: string;
  price: number;
  quantity: number;
  currency: string;
  image?: string;
  variant?: {
    name: string;
    option: string;
  };
  packageId?: string;
  packageName?: string;
  vendorId?: string;
  vendorName?: string;
  subtotal: number;
}

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface BillingAddress extends ShippingAddress {}

export interface OrderChargeItem {
  name: string;
  type?: string;
  value?: number;
  amount: number;
}

export interface OrderTotals {
  subtotal: number;
  discount: number;
  total: number;
  charges?: OrderChargeItem[];
}

export interface OrderPayment {
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  method: 'credit_card' | 'debit_card' | 'paypal' | 'razorpay' | 'cod' | 'mock';
  transactionId?: string;
  paidAt?: string;
  amount: number;
  currency: string;
}

export interface OrderShipping {
  method: 'standard' | 'express' | 'overnight';
  cost: number;
  trackingNumber?: string;
  carrier?: string;
  estimatedDelivery?: string;
  shippedAt?: string;
  deliveredAt?: string;
}

export interface StatusHistory {
  status: string;
  timestamp: string;
  note?: string;
  updatedBy?: string;
}

export interface Order {
  _id?: string;
  orderNumber: string;
  orderName?: string;
  user: string;
  items: OrderItem[];
  shippingAddress: ShippingAddress;
  billingAddress: BillingAddress;
  status:
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'shipped'
    | 'delivered'
    | 'cancelled'
    | 'refunded';
  payment: OrderPayment;
  shipping: OrderShipping;
  totals: OrderTotals;
  couponCode?: string;
  notes?: string;
  cancellation?: {
    reason: string;
    cancelledBy: string;
    cancelledAt: string;
  };
  statusHistory?: StatusHistory[];
  itemCount?: number;
  vendorCount?: number;
  acceptance?: {
    status: 'broadcast' | 'accepted';
    acceptedByVendorId?: string;
    acceptedAt?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateOrderRequest {
  orderNumber?: string;
  orderName?: string;
  shippingAddressId: string;
  billingAddressId?: string;
  paymentMethod?:
    | 'credit_card'
    | 'debit_card'
    | 'paypal'
    | 'razorpay'
    | 'cod'
    | 'mock';
  notes?: string;
  /** When true, order is broadcast to all partners; one partner must Accept to take it */
  broadcast?: boolean;
}

export interface UpdateOrderStatusRequest {
  status:
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'shipped'
    | 'delivered'
    | 'cancelled'
    | 'refunded';
  note?: string;
  trackingNumber?: string;
  carrier?: string;
  otp?: string;
}

export interface CancelOrderRequest {
  reason?: string;
}

export interface OrderFilters {
  status?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface VerifyPaymentRequest {
  razorpayPaymentId: string;
  razorpayOrderId: string;
  razorpaySignature: string;
}

export interface CreateOrderRazorpayResponse {
  success: boolean;
  data: {
    order: Order;
    razorpayOrderId: string;
    razorpayKeyId: string;
  };
  message: string;
}

class OrderService {
  /**
   * Create order from cart. For Razorpay, response.data includes order, razorpayOrderId, razorpayKeyId.
   */
  async createOrder(
    orderData: CreateOrderRequest
  ): Promise<{
    success: boolean;
    data:
      | Order
      | { order: Order; razorpayOrderId: string; razorpayKeyId: string };
    message: string;
  }> {
    const response = await axiosInstance.post('/orders', orderData);
    return response.data;
  }

  /**
   * Verify Razorpay payment after checkout success
   */
  async verifyPayment(
    orderId: string,
    payload: VerifyPaymentRequest
  ): Promise<{ success: boolean; data: Order; message: string }> {
    const response = await axiosInstance.post(
      `/orders/${orderId}/verify-payment`,
      payload
    );
    return response.data;
  }

  /**
   * Get user's orders
   */
  async getOrders(
    filters: OrderFilters = {}
  ): Promise<{ success: boolean; data: { orders: Order[]; pagination: any } }> {
    const response = await axiosInstance.get('/orders', { params: filters });
    return response.data;
  }

  /**
   * Get order by ID
   */
  async getOrderById(
    orderId: string
  ): Promise<{ success: boolean; data: Order }> {
    const response = await axiosInstance.get(`/orders/${orderId}`);
    return response.data;
  }

  /**
   * Update order status (vendor/admin)
   */
  async updateOrderStatus(
    orderId: string,
    statusData: UpdateOrderStatusRequest
  ): Promise<{ success: boolean; data: Order; message: string }> {
    const response = await axiosInstance.patch(
      `/orders/${orderId}/status`,
      statusData
    );
    return response.data;
  }

  /**
   * Cancel order
   */
  async cancelOrder(
    orderId: string,
    cancelData?: CancelOrderRequest
  ): Promise<{ success: boolean; data: Order; message: string }> {
    const response = await axiosInstance.post(
      `/orders/${orderId}/cancel`,
      cancelData || {}
    );
    return response.data;
  }

  /**
   * Get all orders (admin only)
   */
  async getAllOrders(
    filters: OrderFilters & {
      userId?: string;
      vendorId?: string;
      search?: string;
    } = {}
  ): Promise<{ success: boolean; data: { orders: Order[]; pagination: any } }> {
    const response = await axiosInstance.get('/orders/admin/all', {
      params: filters,
    });
    return response.data;
  }

  /**
   * Get vendor orders (includes broadcast orders to accept)
   */
  async getVendorOrders(
    filters: OrderFilters = {}
  ): Promise<{ success: boolean; data: { orders: Order[]; pagination: any } }> {
    const response = await axiosInstance.get('/orders/vendor', {
      params: filters,
    });
    return response.data;
  }

  /**
   * Accept a broadcast order (vendor takes the order)
   */
  async acceptOrder(
    orderId: string
  ): Promise<{ success: boolean; data: Order; message: string }> {
    const response = await axiosInstance.post(`/orders/${orderId}/accept`);
    return response.data;
  }

  /**
   * Request delivery OTP — vendor sends OTP to customer's phone before marking delivered.
   */
  async requestDeliveryOtp(
    orderId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post(
      `/orders/${orderId}/request-delivery-otp`
    );
    return response.data;
  }

  /**
   * Get order receipt (HTML). Use for download/print when order is delivered.
   */
  async getOrderReceipt(orderId: string): Promise<string> {
    const response = await axiosInstance.get(`/orders/${orderId}/receipt`, {
      responseType: 'text',
    });
    return response.data as string;
  }
}

export const orderService = new OrderService();
